import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@tanstack/react-form';
import { Button } from '@/shared/components/ui/Button';
import type { KeyResult } from '@momentum/types';
import { KeyResultType, IssueVisibility } from '@momentum/types';
import { ParentGoalSelector } from './components/ParentGoalSelector';
import { AssigneeSelector } from './components/AssigneeSelector';
import { OwnerSelector } from './components/OwnerSelector';
import { StatusSelector } from './components/StatusSelector';
import { ViewerSelector } from './components/ViewerSelector';
import { VisibilitySelector } from './components/VisibilitySelector';
import { createGoalSchema, type CreateGoalFormData } from './types/goals.types';
import { useAppForm } from '@/shared/components/form/form';
import { KeyResultsField } from './components/KeyResultsField';

interface GoalFormProps {
  initialValues: CreateGoalFormData;
  isPending: boolean;
  onSubmit: (
    formData: Omit<CreateGoalFormData, 'keyResults'> & {
      keyResults?: KeyResult[];
    }
  ) => Promise<void>;
  onCancel?: () => void;
  submitLabel: string;
  submittingLabel: string;
}

export const GoalForm: React.FC<GoalFormProps> = ({
  initialValues,
  isPending,
  onSubmit,
  onCancel,
  submitLabel,
  submittingLabel,
}) => {
  const { t } = useTranslation('goals');

  const form = useAppForm({
    defaultValues: initialValues,
    validators: {
      onChange: createGoalSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const filledKeyResults: KeyResult[] = (value.keyResults ?? []).map(
          (keyResult) => ({
            ...keyResult,
            currentProgress: keyResult.currentProgress ?? 0,
            type: keyResult.type ?? KeyResultType.MAXIMUM,
          })
        );

        await onSubmit({
          ...value,
          keyResults: filledKeyResults,
        });

        form.reset();
      } catch (error) {
        console.error('Failed to submit goal:', error);
      }
    },
  });

  const visibility = useStore(form.store, (state) => state.values.visibility);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto -mx-6 px-6 min-h-0">
        <div className="space-y-6 py-2">
          {/* Main goal information */}
          <form.AppField
            name="title"
            children={(field) => <field.TextField label={t('title')} />}
          />

          <form.AppField
            name="description"
            children={(field) => (
              <field.TextAreaField label={t('description')} rows={2} />
            )}
          />

          <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
            <div className="lg:col-span-5">
              <OwnerSelector form={form} />
            </div>
            <div className="lg:col-span-5">
              <AssigneeSelector form={form} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
            <div className="lg:col-span-5">
              <form.AppField
                name="visibility"
                children={(field) => (
                  <VisibilitySelector field={field} disabled={isPending} />
                )}
              />
            </div>
            <div className="lg:col-span-5">
              <form.AppField
                name="parent"
                children={(field) => (
                  <ParentGoalSelector field={field} disabled={isPending} />
                )}
              />
            </div>
          </div>

          {/* Conditional Viewers Selector */}
          {visibility === IssueVisibility.PRIVATE && (
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
              <div className="lg:col-span-10">
                <form.AppField
                  name="viewers"
                  children={(field) => (
                    <ViewerSelector field={field} disabled={isPending} />
                  )}
                />
              </div>
            </div>
          )}

          {/* Status and Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <form.AppField
              name="startDate"
              children={(field) => <field.DateField label={t('startDate')} />}
            />

            <form.AppField
              name="dueDate"
              children={(field) => <field.DateField label={t('dueDate')} />}
            />

            <form.AppField
              name="status"
              children={(field) => (
                <StatusSelector field={field} disabled={isPending} />
              )}
            />
          </div>

          {/* Key Results Section */}
          <div className="pt-4 pb-6">
            <KeyResultsField form={form} />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 pb-4 border-t border-gray-200 -mx-6 px-6 bg-white">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            {t('cancel')}
          </Button>
        )}
        <Button type="submit" disabled={isPending}>
          {isPending ? submittingLabel : submitLabel}
        </Button>
      </div>
    </form>
  );
};
